// "Report this page" on the branded chat page. A small dialog in the page's
// own colours (CSS variables from brandPageTheme), sending to
// POST /public/pages/:slug/report. Reports reach jawabai's admins, not the
// business. Nothing about the visitor is stored (docs/branded-page.md).
import { useEffect, useRef, useState } from 'react';
import { reportBrandPage } from '../../lib/brandPageApi';

const REASONS = ['scam', 'impersonation', 'offensive', 'other'];

export function ReportLink({ onClick, t }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-[var(--text-muted)] underline-offset-2 hover:underline"
    >
      {t('report.link')}
    </button>
  );
}

export default function ReportDialog({ slug, t, onClose }) {
  const [reason, setReason] = useState('');
  const [details, setDetails] = useState('');
  const [state, setState] = useState('idle'); // idle | sending | done | error
  const [error, setError] = useState('');
  const dialogRef = useRef(null);

  // Escape closes; focus starts inside the dialog.
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    dialogRef.current?.querySelector('input, button')?.focus();
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const submit = async (e) => {
    e.preventDefault();
    if (!reason || state === 'sending') return;
    setState('sending');
    try {
      await reportBrandPage(slug, reason, details.trim());
      setState('done');
    } catch (err) {
      const key =
        { rate_limited: 'rateLimited', offline: 'offline', gone: 'gone' }[err.code] || 'failed';
      setError(t(key));
      setState('error');
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="bp-report-title"
        className="bp-in w-full max-w-md rounded-t-3xl border border-[var(--border)] bg-[var(--surface)] p-6 text-[var(--text)] shadow-2xl sm:rounded-3xl"
      >
        {state === 'done' ? (
          <div className="flex flex-col items-center gap-3 py-4 text-center">
            <h2 id="bp-report-title" className="text-lg font-bold">
              {t('report.thanksTitle')}
            </h2>
            <p className="text-sm text-[var(--text-muted)]">{t('report.thanksBody')}</p>
            <button
              type="button"
              onClick={onClose}
              className="mt-2 rounded-full bg-[var(--brand)] px-5 py-2.5 text-sm font-bold text-[var(--brand-fg)]"
            >
              {t('report.close')}
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="flex flex-col gap-4">
            <div>
              <h2 id="bp-report-title" className="text-lg font-bold">
                {t('report.title')}
              </h2>
              <p className="mt-1 text-sm text-[var(--text-muted)]">{t('report.intro')}</p>
            </div>
            <fieldset className="flex flex-col gap-2">
              <legend className="sr-only">{t('report.reasonLabel')}</legend>
              {REASONS.map((r) => (
                <label
                  key={r}
                  className={`flex cursor-pointer items-center gap-3 rounded-xl border px-3.5 py-2.5 text-sm transition-colors ${
                    reason === r
                      ? 'border-[var(--brand)] bg-[var(--brand-tint)]'
                      : 'border-[var(--border)]'
                  }`}
                >
                  <input
                    type="radio"
                    name="reason"
                    value={r}
                    checked={reason === r}
                    onChange={() => setReason(r)}
                    className="accent-[var(--brand)]"
                  />
                  {t(`report.reasons.${r}`)}
                </label>
              ))}
            </fieldset>
            <label className="flex flex-col gap-1.5 text-sm">
              <span className="text-[var(--text-muted)]">{t('report.detailsLabel')}</span>
              <textarea
                dir="auto"
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                maxLength={500}
                rows={3}
                className="resize-none rounded-xl border border-[var(--border)] bg-[var(--surface-2)] px-3 py-2 text-[var(--text)] outline-none focus:border-[var(--brand)]"
              />
            </label>
            {state === 'error' && (
              <p role="alert" className="text-sm text-[var(--text-muted)]">
                {error}
              </p>
            )}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded-full px-4 py-2.5 text-sm font-semibold text-[var(--text-muted)] hover:text-[var(--text)]"
              >
                {t('report.cancel')}
              </button>
              <button
                type="submit"
                disabled={!reason || state === 'sending'}
                className="rounded-full bg-[var(--brand)] px-5 py-2.5 text-sm font-bold text-[var(--brand-fg)] disabled:opacity-50"
              >
                {state === 'sending' ? t('report.sending') : t('report.send')}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
